import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Badge,
} from '@mui/material';
import { NoteAlt as NoteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useUpdateAnnotation } from '@/hooks/useChannelAnnotations';
import { useChatTheme } from '../ChatThemeContext';

interface ChannelNoteEditorProps {
  chatId: number;
  currentNote: string | null;
  /** `header` paints for the blue chat-preview header; `plain` for a table cell. */
  variant?: 'header' | 'plain';
}

const ChannelNoteEditor: React.FC<ChannelNoteEditorProps> = ({
  chatId,
  currentNote,
  variant = 'header',
}) => {
  const { t } = useTranslation('pages');
  const { colors } = useChatTheme();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(currentNote || '');
  const updateMutation = useUpdateAnnotation();

  const handleOpen = () => {
    setNote(currentNote || '');
    setOpen(true);
  };

  const handleSave = () => {
    updateMutation.mutate(
      { chatId, data: { notes: note } },
      { onSuccess: () => setOpen(false) }
    );
  };

  const hasNote = !!currentNote && currentNote.trim() !== '';

  return (
    <>
      <Tooltip title={t('chatPreview.annotations.editNotes')}>
        <IconButton
          onClick={handleOpen}
          size="small"
          sx={{ color: variant === 'header' ? colors.headerText : 'inherit' }}
        >
          <Badge color="info" variant="dot" invisible={!hasNote}>
            <NoteIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('chatPreview.annotations.editNotes')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('chatPreview.annotations.notesPlaceholder')}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>
            {t('chatPreview.annotations.cancelNotes')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={updateMutation.isPending}
          >
            {t('chatPreview.annotations.saveNotes')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChannelNoteEditor;
